import React, { createContext, useContext, useState, useEffect } from 'react';
import { guardedSupabaseQuery } from '@/utils/supabaseGuard';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthContext';
import { toast } from 'sonner';
import { productionLogger } from '@/utils/production-logger';

interface Favorite {
  id: string;
  product_id: string;
  product_name: string;
  product_image: string | null;
  product_price: string;
  created_at: string;
}

interface FavoritesContextType {
  favorites: Favorite[];
  loading: boolean;
  addToFavorites: (productId: string, productName: string, productImage?: string, productPrice?: string) => Promise<void>;
  removeFromFavorites: (productId: string) => Promise<void>;
  isFavorite: (productId: string) => boolean;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  

  useEffect(() => {
    if (user) {
      fetchFavorites();
    } else {
      setFavorites([]);
      setLoading(false);
    }
  }, [user]);

  const fetchFavorites = async () => {
    if (!user) return;

    try {
      const result = await guardedSupabaseQuery(
        async () => {
          return await supabase
            .from('favorites')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });
        },
        { 
          fallbackData: [],
          allowOffline: true,
          errorMessage: 'Unable to load favorites'
        }
      );

      if (result.isOffline && result.data) {
        setFavorites(result.data);
      } else if (result.error && !result.isOffline) {
        // Silent error handling - no user-facing errors
      } else {
        setFavorites(result.data || []);
      }
    } catch (error) {
      // Silent error handling
    } finally {
      setLoading(false);
    }
  };

  const addToFavorites = async (productId: string, productName: string, productImage?: string, productPrice?: string) => {
    if (!user) {
      toast.error("You need to be signed in to save favorites");
      return;
    }

    try {
      const result = await guardedSupabaseQuery(
        async () => {
          return await supabase
            .from('favorites')
            .insert({
              user_id: user.id,
              product_id: productId,
              product_name: productName,
              product_image: productImage || null,
              product_price: productPrice || ''
            });
        },
        { 
          errorMessage: 'Unable to add to favorites'
        }
      );

      if (result.isOffline) {
        toast.error("Favorites will be synced when connection is restored");
        return;
      }

      if (result.error) {
        if (result.error.message?.includes('23505')) {
          toast.error("This item is already in your favorites");
        } else {
          toast.error("Failed to add to favorites");
        }
      } else {
        toast.success(`${productName} has been saved to your favorites`);
        fetchFavorites();
      }
    } catch (error) {
      toast.error("Failed to add to favorites");
    }
  };

  const removeFromFavorites = async (productId: string) => {
    if (!user) return;

    try {
      const result = await guardedSupabaseQuery(
        async () => {
          return await supabase
            .from('favorites')
            .delete()
            .eq('user_id', user.id)
            .eq('product_id', productId);
        },
        { 
          errorMessage: 'Unable to remove from favorites'
        }
      );

      if (result.isOffline) {
        toast.error("Changes will be synced when connection is restored");
        return;
      }

      if (result.error) {
        toast.error("Failed to remove from favorites");
      } else {
        toast.success("Item has been removed from your favorites");
        fetchFavorites();
      }
    } catch (error) {
      toast.error("Failed to remove from favorites");
    }
  };

  const isFavorite = (productId: string) => {
    return favorites.some(fav => fav.product_id === productId);
  };

  return (
    <FavoritesContext.Provider value={{
      favorites,
      loading,
      addToFavorites,
      removeFromFavorites,
      isFavorite,
    }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    // Return safe defaults instead of throwing to prevent app crashes during initialization
    productionLogger.warn('useFavorites called outside of FavoritesProvider, returning default values');
    return {
      favorites: [],
      addToFavorites: () => Promise.resolve(),
      removeFromFavorites: () => Promise.resolve(),
      isFavorite: () => false,
      loading: false
    };
  }
  return context;
};